import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined"
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined"
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined"
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControlLabel from "@mui/material/FormControlLabel"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"
import { Link as RouterLink, useNavigate } from "react-router-dom"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import {
  MULTIPART_HEADERS,
  useAuthorizedAxiosClient,
} from "../../../common/axios"
import Column from "../../../common/components/Column"
import LengthLimitedTextField from "../../../common/components/LengthLimitedTextField"
import Row from "../../../common/components/Row"
import AddAttachmentsSection, {
  useAddAttachmentSectionState,
} from "../components/AddAttachmentsSection"
import DialogHeader from "../components/DialogHeader"
import EmailWYSWIGEditor, {
  useEmailWYSWIGEditorState,
} from "../components/EmailWYSWIGEditor"
import InboxTemplateAIGenDialog from "../components/InboxTemplateAIGenDialog"
import InboxTemplateStatusChip from "../components/InboxTemplateStatusChip"
import {
  CONCIERGE_EMAIL_SIGNATURE_API_URL,
  buildSignaturePreviewHtml,
  isEditorBodyEmpty,
  parseIncludeSignature,
} from "../components/emailSignatureUtils"
import {
  INBOX_TEMPLATE_AI_REVIEW_NOTE,
  INBOX_TEMPLATE_AI_REVIEW_READY_NOTE,
  improveEmailInboxTemplate,
} from "../components/inboxTemplateAi"
import { getEmailEditorHTML } from "../email-campaigns/edit-email-mixin"
import { AUTOCOMPLETE_OPTIONS, useDialogContainer } from "../utils"
import { canEditResource } from "../utils/resourceAccess"
import { SETTINGS_TABS, createSettingsTabURL } from "./index"

const TEMPLATES_URL = "/crm/api/zoho-mail/inbox/templates/"

export default function InboxTemplatesDrawer({ open, onClose }) {
  const axios = useAuthorizedAxiosClient()
  const Container = useDialogContainer()
  const [templates, setTemplates] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [editing, setEditing] = useState(null)
  const [creating, setCreating] = useState(false)
  const [listError, setListError] = useState("")

  const loadTemplates = useCallback(() => {
    setIsLoading(true)
    return axios
      .get(TEMPLATES_URL, { params: { scope: "account" } })
      .then(({ data }) => {
        setTemplates(Array.isArray(data) ? data : data.results || [])
      })
      .catch(() => setTemplates([]))
      .finally(() => setIsLoading(false))
  }, [axios])

  useEffect(() => {
    if (!open) {
      setCreating(false)
      setEditing(null)
      setListError("")
      return undefined
    }
    loadTemplates()
    return undefined
  }, [open, loadTemplates])

  function handleSoftClose() {
    if (creating || editing) {
      setCreating(false)
      setEditing(null)
      return
    }
    onClose()
  }

  const showForm = creating || Boolean(editing)

  return (
    <Container
      open={open}
      onClose={() => onClose()}
      anchor="right"
      PaperProps={{
        sx: { width: { xs: "100%", sm: 560 }, maxWidth: "100%" },
      }}
    >
      <DialogHeader
        title={
          creating
            ? "New inbox template"
            : editing
            ? "Edit inbox template"
            : "Inbox reply templates"
        }
        onClose={handleSoftClose}
      />
      <DialogContent>
        {showForm ? (
          <InboxTemplateForm
            template={editing}
            onCancel={() => {
              setCreating(false)
              setEditing(null)
            }}
            onSaved={() => {
              setCreating(false)
              setEditing(null)
              loadTemplates()
            }}
            onError={setListError}
          />
        ) : (
          <Column sx={{ gap: 2 }}>
            {listError ? (
              <Alert severity="error" onClose={() => setListError("")}>
                {listError}
              </Alert>
            ) : null}
            <Typography variant="body2" sx={{ color: "colors.gray600" }}>
              These templates belong to this account and are only used when
              replying to inbound inbox mail. Run AI review, verify the updated
              content, then save. Templates are available immediately after
              save. Your account owner can reject or deactivate them later.
              Default templates from admin are also available in the reply
              picker and cannot be edited here.
            </Typography>
            <Row sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<AddOutlinedIcon />}
                onClick={() => setCreating(true)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Add template
              </Button>
            </Row>
            {isLoading ? (
              <Typography variant="body2" sx={{ color: "colors.gray500" }}>
                Loading templates…
              </Typography>
            ) : templates.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: "1px dashed",
                  borderColor: "colors.gray200",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" sx={{ color: "colors.gray600" }}>
                  No inbox reply templates yet. Add one to reuse it when
                  answering inbound mail.
                </Typography>
              </Paper>
            ) : (
              templates.map((template) => (
                <TemplateListItem
                  key={template.id}
                  template={template}
                  onEdit={() => setEditing(template)}
                  onDeleted={loadTemplates}
                  onError={setListError}
                />
              ))
            )}
          </Column>
        )}
      </DialogContent>
    </Container>
  )
}

function TemplateListItem({ template, onEdit, onDeleted, onError }) {
  const axios = useAuthorizedAxiosClient()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const attachmentCount =
    template.attachments?.length ?? template.attachment_count ?? 0

  function handleDelete() {
    setIsDeleting(true)
    axios
      .delete(`${TEMPLATES_URL}${template.id}/`)
      .then(() => {
        setConfirmOpen(false)
        onDeleted()
      })
      .catch(({ response }) => {
        onError?.(
          parseApiErrorMessage(
            response?.data,
            "Could not delete inbox template"
          )
        )
      })
      .finally(() => setIsDeleting(false))
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "colors.gray200",
          borderRadius: 2,
        }}
      >
        <Row
          sx={{
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Column sx={{ minWidth: 0, gap: 0.5 }}>
            <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography fontWeight={700} color="text.primary">
                {template.name}
              </Typography>
              <InboxTemplateStatusChip status={template.approval_status} />
            </Row>
            <Typography variant="body2" sx={{ color: "colors.gray600" }} noWrap>
              {template.subject || "No subject"}
            </Typography>
            {attachmentCount > 0 ? (
              <Row sx={{ alignItems: "center", gap: 0.5 }}>
                <AttachFileOutlinedIcon
                  sx={{ fontSize: 16, color: "colors.gray500" }}
                />
                <Typography variant="caption" sx={{ color: "colors.gray500" }}>
                  {attachmentCount} attachment{attachmentCount === 1 ? "" : "s"}
                </Typography>
              </Row>
            ) : null}
          </Column>
          <Row sx={{ gap: 0.5 }}>
            {canEditResource(template) ? (
              <>
                <Button
                  size="small"
                  onClick={onEdit}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setConfirmOpen(true)}
                  aria-label="Delete template"
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                View only
              </Typography>
            )}
          </Row>
        </Row>
      </Paper>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography>
            Delete inbox template <strong>{template.name}</strong>? This only
            affects inbound inbox replies.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={isDeleting}
            onClick={handleDelete}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

function InboxTemplateForm({ template, onCancel, onSaved, onError }) {
  const axios = useAuthorizedAxiosClient()
  const navigate = useNavigate()
  const emailSettingsURL = createSettingsTabURL(SETTINGS_TABS.configureEmail)
  const [name, setName] = useState(template?.name || "")
  const [subject, setSubject] = useState(template?.subject || "")
  const [includeSignature, setIncludeSignature] = useState(
    parseIncludeSignature(template?.include_signature)
  )
  const [signaturePreviewHtml, setSignaturePreviewHtml] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [isAiReviewing, setIsAiReviewing] = useState(false)
  const [saveError, setSaveError] = useState("")
  const [aiReviewToken, setAiReviewToken] = useState("")
  const [aiReviewedDraft, setAiReviewedDraft] = useState(null)
  const [aiPrompt, setAiPrompt] = useState("")
  const [aiGenDialogOpen, setAiGenDialogOpen] = useState(false)
  const [editorHtml, setEditorHtml] = useState(template?.html_body || "")
  const {
    editorReady,
    setEditorReady,
    insertLinkDialogOpen,
    setInsertLinkDialogOpen,
    editorRef,
    setEditorBody,
  } = useEmailWYSWIGEditorState(true)
  const {
    fileAttachments,
    setFileAttachments,
    fileAttachmentError,
    setFileAttachmentError,
    setDeletedAttachments,
    addAttachmentsToFormData,
    convertBackendAttachmentsToFileAttachments,
  } = useAddAttachmentSectionState()

  useEffect(() => {
    axios
      .get(CONCIERGE_EMAIL_SIGNATURE_API_URL)
      .then(({ data }) =>
        setSignaturePreviewHtml(buildSignaturePreviewHtml(data))
      )
      .catch(() => setSignaturePreviewHtml(""))
  }, [axios])

  useEffect(() => {
    if (!editorReady) return
    setEditorBody(
      template?.html_body || "",
      parseIncludeSignature(template?.include_signature),
      signaturePreviewHtml
    )
    convertBackendAttachmentsToFileAttachments(template?.attachments || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorReady, template?.id, signaturePreviewHtml])

  function invalidateAiReview() {
    setAiReviewToken("")
    setAiReviewedDraft(null)
  }

  function applyReviewedDraft(reviewedDraft, token, prompt = "") {
    const nextDraft = {
      subject: reviewedDraft.subject || "",
      html_body: reviewedDraft.html_body || "",
    }
    const templateName = reviewedDraft.name || reviewedDraft.subject || ""
    if (templateName) {
      setName(templateName)
    }
    setSubject(nextDraft.subject)
    setEditorHtml(nextDraft.html_body)
    if (editorReady) {
      setEditorBody(nextDraft.html_body, includeSignature, signaturePreviewHtml)
    }
    setAiReviewToken(token)
    setAiReviewedDraft(nextDraft)
    setAiPrompt(prompt)
  }

  function handleAiApply({
    draft: reviewedDraft,
    aiReviewToken,
    aiPrompt: prompt,
  }) {
    applyReviewedDraft(reviewedDraft, aiReviewToken, prompt || "")
  }

  const isReadyToSave = Boolean(aiReviewToken && aiReviewedDraft)

  async function handleAiReview() {
    const html = editorRef.current ? getEmailEditorHTML(editorRef) : editorHtml
    if (!name.trim() || isEditorBodyEmpty(html)) return

    setSaveError("")
    setIsAiReviewing(true)
    try {
      const reviewed = await improveEmailInboxTemplate(axios, {
        subject: subject.trim() || name.trim(),
        html_body: html,
      })
      applyReviewedDraft(
        {
          subject: reviewed.subject,
          html_body: reviewed.html_body,
        },
        reviewed.aiReviewToken,
        ""
      )
    } catch ({ response }) {
      const message = parseApiErrorMessage(
        response?.data,
        "Couldn't review that template. Please try again."
      )
      setSaveError(message)
      onError?.(message)
    } finally {
      setIsAiReviewing(false)
    }
  }

  function handleSave() {
    if (!isReadyToSave) return
    if (includeSignature && !signaturePreviewHtml) {
      navigate(emailSettingsURL)
      return
    }

    setSaveError("")
    const reviewedSubject = aiReviewedDraft.subject
    const reviewedHtml = aiReviewedDraft.html_body
    const reviewToken = aiReviewToken
    const reviewPrompt = aiPrompt

    const formData = new FormData()
    formData.append("name", name.trim())
    formData.append("subject", reviewedSubject)
    formData.append("html_body", reviewedHtml)
    formData.append("include_signature", includeSignature ? "true" : "false")
    formData.append("ai_review_token", reviewToken)
    formData.append("ai_prompt", reviewPrompt)
    addAttachmentsToFormData(formData)
    setIsSaving(true)
    const request = template
      ? axios.put(
          `${TEMPLATES_URL}${template.id}/`,
          formData,
          MULTIPART_HEADERS
        )
      : axios.post(TEMPLATES_URL, formData, MULTIPART_HEADERS)
    request
      .then(() => onSaved())
      .catch(({ response }) => {
        const message = parseApiErrorMessage(
          response?.data,
          "Could not save inbox template"
        )
        setSaveError(message)
        onError?.(message)
        invalidateAiReview()
      })
      .finally(() => setIsSaving(false))
  }

  const canReview = Boolean(name.trim()) && editorReady
  const canSave = isReadyToSave && !(includeSignature && !signaturePreviewHtml)

  return (
    <Column sx={{ gap: 2 }}>
      <Alert severity="info">
        {isReadyToSave
          ? INBOX_TEMPLATE_AI_REVIEW_READY_NOTE
          : INBOX_TEMPLATE_AI_REVIEW_NOTE}
      </Alert>
      {saveError ? (
        <Alert severity="error" onClose={() => setSaveError("")}>
          {saveError}
        </Alert>
      ) : null}
      <Row
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={onCancel}
          sx={{ textTransform: "none" }}
        >
          Back to templates
        </Button>
        {!template ? (
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeOutlinedIcon />}
            onClick={() => setAiGenDialogOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            AI generate
          </Button>
        ) : null}
      </Row>
      <LengthLimitedTextField
        label="Template name"
        value={name}
        setValue={setName}
        maxLength={128}
        required
      />
      <LengthLimitedTextField
        label="Subject"
        helperText="Used to identify this template. Replies still keep the original thread subject."
        value={subject}
        setValue={(value) => {
          invalidateAiReview()
          setSubject(value)
        }}
        maxLength={988}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={includeSignature}
            onChange={(event) => setIncludeSignature(event.target.checked)}
          />
        }
        label="Include email signature when sending this template"
      />
      {includeSignature && !signaturePreviewHtml ? (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          Email signature is required before you can save a template with
          signature enabled.{" "}
          <Link
            component={RouterLink}
            to={emailSettingsURL}
            onClick={(event) => {
              event.preventDefault()
              navigate(emailSettingsURL)
            }}
          >
            Set up your signature in Settings → Email
          </Link>
          .
        </Typography>
      ) : null}
      <EmailWYSWIGEditor
        height={280}
        editorRef={editorRef}
        editorReady={editorReady}
        setEditorReady={setEditorReady}
        insertLinkDialogOpen={insertLinkDialogOpen}
        setInsertLinkDialogOpen={setInsertLinkDialogOpen}
        includeSignature={includeSignature}
        signaturePreviewHtml={signaturePreviewHtml}
        onContentChange={() => invalidateAiReview()}
        onSetup={(editor) =>
          editor.ui.registry.addMenuButton("insert-agent-data", {
            text: "Insert placeholder",
            fetch: (callback) => {
              callback(
                AUTOCOMPLETE_OPTIONS.map((option) => ({
                  type: "menuitem",
                  text: option.label,
                  onAction: () =>
                    editor.insertContent(`{{ ${option.fieldName} }}`),
                }))
              )
            },
          })
        }
      />
      <AddAttachmentsSection
        fileAttachments={fileAttachments}
        setFileAttachments={setFileAttachments}
        fileAttachmentError={fileAttachmentError}
        setFileAttachmentError={setFileAttachmentError}
        setDeletedAttachments={setDeletedAttachments}
        maxAttachmentCount={null}
      />
      <Row sx={{ justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
        <Button variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        {!isReadyToSave ? (
          <LoadingButton
            variant="contained"
            loading={isAiReviewing}
            disabled={!canReview}
            startIcon={<AutoAwesomeOutlinedIcon />}
            onClick={handleAiReview}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {isAiReviewing ? "Reviewing with AI…" : "Review with AI"}
          </LoadingButton>
        ) : (
          <LoadingButton
            variant="contained"
            loading={isSaving}
            disabled={!canSave}
            onClick={handleSave}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {template ? "Save template" : "Add template"}
          </LoadingButton>
        )}
      </Row>
      <InboxTemplateAIGenDialog
        open={aiGenDialogOpen}
        medium="email"
        onApply={handleAiApply}
        onClose={() => setAiGenDialogOpen(false)}
      />
    </Column>
  )
}
